'use client'

import { useCallback, useMemo } from 'react'

import type { Notification } from '~/data/api/client'
import { useGetOpsNotificationsQuery, useMarkAsReadMutation } from '~/data/api/client'

const ASSISTANT_NOTIFICATION_TYPES = new Set([
  'ASSISTANT_ACTION_COMPLETE',
  'ASSISTANT_DIAGNOSTICS_COMPLETE',
  'ASSISTANT_SCHEDULE_RUN',
  'TASK_DELEGATION',
  // Firmware rollouts reuse the ops-notifications inbox so operators can jump
  // back to a durable rollout ID from the assistant bar.
  'FIRMWARE_ROLLOUT_COMPLETE',
  'FIRMWARE_ROLLOUT_NEEDS_ATTENTION'
])

const POLL_INTERVAL_MS = 60_000

export default function useOpsNotifications(enabled: boolean) {
  const { data, refetch } = useGetOpsNotificationsQuery(
    { unreadOnly: true, limit: 20 },
    { skip: !enabled, pollingInterval: enabled ? POLL_INTERVAL_MS : 0 }
  )

  const [markAsReadMutation] = useMarkAsReadMutation()

  const notifications = useMemo(
    () =>
      (data?.data ?? []).filter((item: Notification) => ASSISTANT_NOTIFICATION_TYPES.has(item.type ?? '')),
    [data?.data]
  )

  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications])

  const refresh = useCallback(async () => {
    if (!enabled) return
    await refetch()
  }, [enabled, refetch])

  const markRead = useCallback(
    async (notificationId: string) => {
      await markAsReadMutation({ notificationId }).unwrap()
    },
    [markAsReadMutation]
  )

  return { notifications, unreadCount, refresh, markRead }
}
